import { Grid } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import joinAndNormalizeUrl from '../../../../server/utils/joinAndNormalizeUrl';
import { type WebpageEndpointContextDataFragment } from '../../../api';
import { useRouter } from '../../../components/contexts/RouterContextManager';
import LazySelectLanguage from '../../../components/LazySelectLanguage';
import { useRuntimeConfig } from '../../../runtimeConfig';
import { BookButton, Container, Email, Phone } from './ui';

// append '/' to pathname if it doesn't exist
const appendSlashToPathname = (pathname: string) => {
    if (pathname !== '/' && !pathname.endsWith('/')) {
        return `${pathname}/`;
    }

    return pathname;
};

const BasicProRightContent = () => {
    const { t } = useTranslation('common');
    const { instance } = useRuntimeConfig();

    const router = useRouter();
    const { pathname } = useLocation();
    const screens = Grid.useBreakpoint();

    const endpoint = useMemo(
        () =>
            router.endpoints.find(item => {
                if (item.__typename === 'WebPageEndpoint') {
                    return item.webpagePaths.some(path => {
                        const endpointPath = `${appendSlashToPathname(item.pathname)}${path.webpageSubpath}`;

                        return (
                            new RegExp(`^/?${pathname}/?$`, 'i').test(endpointPath) ||
                            new RegExp(`^/?${endpointPath}/?$`, 'i').test(pathname)
                        );
                    });
                }

                if (item.__typename === 'MobilityApplicationEntrypoint') {
                    return (
                        new RegExp(`/?${pathname}/?`, 'gi').test(item.pathname) ||
                        new RegExp(`/?${item.pathname}/?`, 'gi').test(pathname)
                    );
                }

                return false;
            }),
        [router.endpoints, pathname]
    );

    // retrieve contact info from website module
    const { phone, email } = useMemo(() => {
        if (!endpoint) {
            return { phone: undefined, email: undefined };
        }

        if (endpoint.__typename === 'WebPageEndpoint') {
            return { phone: endpoint.websiteModule?.phone, email: endpoint.websiteModule?.email };
        }
        if (endpoint.__typename === 'MobilityApplicationEntrypoint') {
            // filter router.endpoints for the first Web Page endpoint found
            const websiteEndpoint = (router?.endpoints ?? []).find(
                item => item.__typename === 'WebPageEndpoint'
            ) as WebpageEndpointContextDataFragment;

            return { phone: websiteEndpoint?.websiteModule?.phone, email: websiteEndpoint?.websiteModule?.email };
        }

        // endpoint not supported
        return { phone: undefined, email: undefined };
    }, [endpoint, router]);

    const mobilityApplicationUrl = useMemo(() => {
        if (!router) {
            return null;
        }

        const mobilityPathName = (router.endpoints ?? []).find(
            item => item.__typename === 'MobilityApplicationEntrypoint'
        )?.pathname;

        if (!mobilityPathName) {
            return null;
        }

        return joinAndNormalizeUrl(window.location.origin, router.pathname, mobilityPathName);
    }, [router]);

    if (instance !== 'psme' || (!phone && !email)) {
        return <LazySelectLanguage />;
    }

    return (
        <Container>
            {phone && <Phone phoneNumber={`+${phone.prefix}${phone.value}`} showTitle={screens.xl} />}
            {email && screens.xl && <Email email={email} />}
            <LazySelectLanguage className="header-info-item psme-select-language" />
            {endpoint.__typename === 'WebPageEndpoint' && mobilityApplicationUrl && (
                <BookButton mobilityApplicationUrl={mobilityApplicationUrl} title={t('common:actions.bookNow')} />
            )}
        </Container>
    );
};

export default BasicProRightContent;
